import { useFormContext, useWatch } from "react-hook-form";
import { t } from "@/i18n-js/instance";
import { useLandingPageSettingsApi } from "@/react/hooks/home_page/useLandingPageSettingsApi";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { useBoolean } from "@circle-react/hooks/utils/useBoolean";
import { ConfirmationModal } from "@circle-react-shared/ModalsV2/ConfirmationModal";
import { Form } from "@circle-react-uikit/Form";
import { RichTextInput } from "@circle-react-uikit/RichTextInput";
import { ToggleSwitchWithLabel } from "@circle-react-uikit/ToggleSwitchWithLabel";
import { CoverImage } from "../CoverImage";

const i18nRoot = "home_page.edit_form.welcome_banner";

interface WelcomeBannerFormProps {
  isLoggedIn: boolean;
}

export const WelcomeBannerForm = ({ isLoggedIn }: WelcomeBannerFormProps) => {
  const title = `${isLoggedIn ? "logged_in" : "logged_out"}_title`;
  const coverImage = `${isLoggedIn ? "logged_in" : "logged_out"}_media`;
  const description = `${isLoggedIn ? "logged_in" : "logged_out"}_description`;

  const { reSetWelcomeBannerPreference } = useLandingPageSettingsApi();
  const [shouldShowConfirmationModal, toggleShouldShowConfirmationModal] =
    useBoolean(false);

  const { setValue } = useFormContext();

  const loggedInBannerEnabled = useWatch({ name: "logged_in_banner_enabled" });
  const loggedOutBannerEnabled = useWatch({
    name: "logged_out_banner_enabled",
  });

  const bannerEnabled = isLoggedIn
    ? loggedInBannerEnabled
    : loggedOutBannerEnabled;

  const bannerField = isLoggedIn
    ? "logged_in_banner_enabled"
    : "logged_out_banner_enabled";

  const bannerLabel = isLoggedIn
    ? "enable_for_logged_in_members"
    : "enable_for_logged_out_members";

  return (
    <>
      <ToggleSwitchWithLabel
        label={t([i18nRoot, bannerLabel])}
        field={bannerField}
        value={bannerEnabled}
        onChange={() => {
          setValue(bannerField, !bannerEnabled);
        }}
        className="mb-2 text-lg font-bold"
        variant="large"
      />
      <div
        className={classNames("mx-auto mt-8 flex h-full flex-col", {
          "cursor-not-allowed opacity-20": !bannerEnabled,
        })}
        data-testid="welcome-banner-form"
      >
        <CoverImage
          title={t([i18nRoot, "cover_image"])}
          name={coverImage}
          description={t([i18nRoot, "cover_image_description"])}
          disabled={!bannerEnabled}
        />
        <div className="-mt-1">
          <Form.Item
            name={title}
            hideDescription
            translationRoot={i18nRoot}
            variant="extra-bottom-margin"
            hideBorder
            fullWidth
            hidePlaceholder
          >
            <Form.Input disabled={!bannerEnabled} />
          </Form.Item>
        </div>
        <div className="-mt-1">
          <Form.Item
            name={description}
            hideDescription
            translationRoot={i18nRoot}
            variant="extra-bottom-margin"
            hideBorder
            fullWidth
            hidePlaceholder
          >
            <div className="trix-v2 trix-v2--modal-3">
              <RichTextInput
                id={description}
                name={description}
                className="min-h-[100px]"
              />
            </div>
          </Form.Item>
        </div>
        {isLoggedIn && (
          <div className="flex flex-col">
            <Form.Item
              name="allow_close"
              description={
                <div className="pr-8">
                  {t([i18nRoot, "allow_close_description"])}
                  <button
                    type="button"
                    onClick={() => {
                      bannerEnabled && toggleShouldShowConfirmationModal();
                    }}
                    className="ml-1 cursor-pointer text-sm underline hover:opacity-80"
                  >
                    {t([i18nRoot, "reset_preference_title"])}
                  </button>
                </div>
              }
              translationRoot={i18nRoot}
              variant="default"
              inlineReverse
              hideBorder
              fullWidth
              hidePlaceholder
              className="!items-start"
            >
              <Form.ToggleSwitch
                name="allow_close"
                variant="small"
                wrapperClass="!mt-1"
              />
            </Form.Item>
          </div>
        )}
        <ConfirmationModal
          isOpen={shouldShowConfirmationModal}
          onClose={toggleShouldShowConfirmationModal}
          onConfirm={async () => {
            await reSetWelcomeBannerPreference();
            toggleShouldShowConfirmationModal();
            window.location.reload();
          }}
          confirmText={t("confirm")}
          title={t([i18nRoot, "reset_preference_title"])}
        >
          <div className="mb-6">
            {t([i18nRoot, "reset_preference_description"])}
          </div>
        </ConfirmationModal>
      </div>
    </>
  );
};
